/** The install hint's memory. Every access is wrapped: storage can be missing or throw (private mode, blocked site data). */
export const HINT_KEY = 'cadinu.pwa.installHint'
export const HINT_SESSION_KEY = 'cadinu.pwa.installHintSession'

export type HintState = 'shown' | 'dismissed' | 'installed'

export function readHint(): HintState | null {
  try {
    const v = JSON.parse(window.localStorage.getItem(HINT_KEY) || 'null') as { state?: HintState } | null
    return v?.state ?? null
  } catch {
    return null
  }
}

export function writeHint(state: HintState) {
  try {
    window.localStorage.setItem(HINT_KEY, JSON.stringify({ state, at: Date.now() }))
  } catch {
    /* not remembered: the component keeps the state in memory */
  }
}

/** true when the hint was first shown in this browsing session (it stays during the visit, then never again) */
export function shownThisSession(): boolean {
  try {
    return window.sessionStorage.getItem(HINT_SESSION_KEY) === '1'
  } catch {
    return false
  }
}

export function markShownThisSession() {
  try {
    window.sessionStorage.setItem(HINT_SESSION_KEY, '1')
  } catch {
    /* ignore */
  }
}

/**
 * First visit or a later one (site review 59 #2, 2026-10-07): the install line no longer greets a first visit, where it pushed the swap
 * button under the bottom bar. The device keeps when it first came (VISIT_KEY); the tab keeps what this visit is (VISIT_SESSION_KEY), so a
 * reload or a client-side page change stays the same visit. Storage that throws (private mode, blocked site data) reads as 'unknown'.
 */
export const VISIT_KEY = 'cadinu.pwa.firstVisit'
export const VISIT_SESSION_KEY = 'cadinu.pwa.visit'
export type VisitKind = 'first' | 'returning' | 'unknown'

export function visitKind(): VisitKind {
  try {
    const known = window.sessionStorage.getItem(VISIT_SESSION_KEY)
    if (known === 'first' || known === 'returning') return known
    const firstAt = window.localStorage.getItem(VISIT_KEY)
    const kind: VisitKind = firstAt ? 'returning' : 'first'
    if (!firstAt) window.localStorage.setItem(VISIT_KEY, String(Date.now()))
    window.sessionStorage.setItem(VISIT_SESSION_KEY, kind)
    return kind
  } catch {
    return 'unknown'
  }
}
